import { ref, watch, getCurrentInstance } from '@vue/composition-api'
import { useActions } from 'vuex-composition-helpers'
import { mdiAbacus, mdiAccountOutline, mdiCogOutline, mdiDnsOutline } from '@mdi/js'
import { subject } from '@casl/ability'
import { PRIVATE } from '@/plugins/acl/const'
import {
  areQueriesEqual,
  didSearchOrFilterValuesChange,
  parseFilterArrayFromQuery,
  parseSearchFromQuery,
  parseTableOptionsFromQuery,
  readListQueryFromSession,
  resetTablePageOnFilterChange,
  shouldResetTablePageAfterFetch,
  serializeFilterArrayToQuery,
  serializeSearchToQuery,
  serializeTableOptionsToQuery,
  shouldRestoreListStateFromUrlQuery,
  writeListQueryToSession,
} from '@/utils/listRouteState'

const LIST_ROUTE_NAME = 'recruitment-jobs-list'

export default function useJobList() {
  const vm = getCurrentInstance().proxy

  const jobs = ref([])
  const choices = ref({})
  const recruiterChoices = ref([])

  const stageFilter = ref([])
  const workflowFilter = ref([])
  const cityFilter = ref([])
  const assignedToFilter = ref([])

  const tableColumns = [
    { text: 'title', value: 'title', class: 'ps-7 text-uppercase' },
    { text: 'workflow', value: 'workflowTitle', class: 'text-uppercase' },
    { text: 'city', value: 'city', class: 'text-uppercase' },
    { text: 'country', value: 'country', class: 'text-uppercase' },
    { text: 'createdBy', value: 'createdBy', sortable: false, class: 'text-uppercase' },
    { text: 'candidates', value: 'candidatesAmount', align: 'center', class: 'text-uppercase' },
    { text: 'status', value: 'stage', class: 'text-uppercase' },
    {
      text: 'actions',
      value: 'actions',
      align: 'center',
      sortable: false,
      class: 'text-uppercase',
    },
  ]

  const searchQuery = ref('')
  const totalJobsListTable = ref(0)
  const loading = ref(false)
  const options = ref({
    sortDesc: [true],
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
  })
  const overview = ref([])
  const isSyncingFromRoute = ref(false)

  const { getJobsFilters, getAllJobs, getJobsOverview } = useActions('jobs', [
    'getJobsFilters',
    'getAllJobs',
    'getJobsOverview',
  ])
  const { getRecruitersChoices } = useActions('company', ['getRecruitersChoices'])

  const fetchJobsOverview = async () => {
    overview.value = await getJobsOverview()
  }

  const applyStateFromRoute = () => {
    isSyncingFromRoute.value = true

    let query = { ...vm.$route.query }
    if (shouldRestoreListStateFromUrlQuery(query)) {
      const stored = readListQueryFromSession(LIST_ROUTE_NAME)
      if (stored) {
        query = { ...stored }
      }
    }

    stageFilter.value = parseFilterArrayFromQuery(query, 'stage')
    workflowFilter.value = parseFilterArrayFromQuery(query, 'workflow')
    cityFilter.value = parseFilterArrayFromQuery(query, 'city')
    assignedToFilter.value = parseFilterArrayFromQuery(query, 'assignedTo')
    searchQuery.value = parseSearchFromQuery(query)
    options.value = parseTableOptionsFromQuery(query, options.value)

    isSyncingFromRoute.value = false
    void syncStateToRoute()
  }

  const syncStateToRoute = async () => {
    if (isSyncingFromRoute.value) return

    const nextQuery = {
      ...serializeTableOptionsToQuery(options.value),
      ...serializeFilterArrayToQuery('stage', stageFilter.value),
      ...serializeFilterArrayToQuery('workflow', workflowFilter.value),
      ...serializeFilterArrayToQuery('city', cityFilter.value),
      ...serializeFilterArrayToQuery('assignedTo', assignedToFilter.value),
      ...serializeSearchToQuery(searchQuery.value),
    }

    if (!areQueriesEqual(vm.$route.query, nextQuery)) {
      await vm.$router.replace({ query: nextQuery })
    }
    writeListQueryToSession(LIST_ROUTE_NAME, nextQuery)
  }

  const normalizeJobFilterChoices = (raw) => {
    const base = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}
    const stageList = Array.isArray(base.stageChoices) ? base.stageChoices : []

    return {
      ...base,
      stageChoices: stageList.map((stage) => ({
        ...stage,
        text: vm.$t(stage.text),
      })),
      recruiterChoices: Array.isArray(base.recruiterChoices) ? base.recruiterChoices : [],
      cityChoices: Array.isArray(base.cityChoices) ? base.cityChoices : [],
      workflowChoices: Array.isArray(base.workflowChoices) ? base.workflowChoices : [],
    }
  }

  const fetchJobs = async (isRetry = false) => {
    loading.value = true
    try {
      const { data, success } = await getAllJobs({
        stage: stageFilter.value,
        assignedTo: assignedToFilter.value,
        city: cityFilter.value,
        workflow: workflowFilter.value,
        search: searchQuery.value,
        page: options.value.page,
        pageSize: options.value.itemsPerPage,
        ordering: options.value.sortBy.map((el, index) => {
          const parsedEl = el
            .replaceAll('.', '__')
            .replace('candidatesAmount', 'candidates_amount')
            .replace('workflowTitle', 'workflow__title')

          return `${options.value.sortDesc[index] ? '-' : ''}${parsedEl}`
        }),
      })
      let total = 0
      if (success && data) {
        if (Object.hasOwnProperty.bind(data)('results') && Object.hasOwnProperty.bind(data)('count')) {
          jobs.value = data.results.map((object) => subject(PRIVATE.ATS.JOB, object))
          totalJobsListTable.value = data.count
          total = data.count
        } else if (Array.isArray(data)) {
          jobs.value = data.map((object) => subject(PRIVATE.ATS.JOB, object))
          totalJobsListTable.value = data.length
          total = data.length
        } else {
          jobs.value = []
          totalJobsListTable.value = 0
        }
      } else {
        jobs.value = []
        totalJobsListTable.value = 0
      }

      if (
        !isRetry &&
        shouldResetTablePageAfterFetch({
          success,
          page: options.value.page,
          total,
          pageSize: options.value.itemsPerPage,
        })
      ) {
        options.value = { ...options.value, page: 1 }
        await syncStateToRoute()
        await fetchJobs(true)
        return
      }

      const { data: filtersData, success: filtersSuccess } = await getJobsFilters({
        stage: stageFilter.value,
        assignedTo: assignedToFilter.value,
        city: cityFilter.value,
        workflow: workflowFilter.value,
      })
      choices.value = normalizeJobFilterChoices(filtersSuccess ? filtersData : {})
    } finally {
      loading.value = false
    }
  }

  const timer = ref(null)
  const delaySearchHandler = async () => {
    clearTimeout(timer.value)
    timer.value = setTimeout(async () => {
      if (options.value.page !== 1) {
        options.value = { ...options.value, page: 1 }
      }
      await syncStateToRoute()
      await fetchJobs()
    }, 1000)
  }

  applyStateFromRoute()

  watch(
    [stageFilter, workflowFilter, cityFilter, assignedToFilter, options],
    async ([stage, workflow, city, assignedTo], oldValues) => {
      const filterChanged = didSearchOrFilterValuesChange({
        filterArrays: [stage, workflow, city, assignedTo],
        oldFilterArrays: oldValues ? oldValues.slice(0, 4) : [],
      })
      if (resetTablePageOnFilterChange(options, filterChanged)) {
        await syncStateToRoute()
        return
      }
      await syncStateToRoute()
      await fetchJobs()
    },
  )

  const fetchRecruitersChoices = async () => {
    recruiterChoices.value = await getRecruitersChoices()
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveJobStageColor = (stage) => {
    if (stage === 'draft') return { text: vm.$t(stage), color: 'secondary' }
    if (stage === 'ongoing') return { text: vm.$t(stage), color: 'primary' }
    if (stage === 'finished') return { text: vm.$t(stage), color: 'accent' }
    if (stage === 'archived') return { text: vm.$t(stage), color: 'primary_c5' }

    return { text: '-/-', color: 'primary' }
  }

  const resolveJobsTotalIcon = (title) => {
    if (title === 'total') return { icon: mdiCogOutline, color: 'secondary' }
    if (title === 'candidatesInOngoingJobs') return { icon: mdiDnsOutline, color: 'primary' }
    if (title === 'ongoing') return { icon: mdiAccountOutline, color: 'success' }
    if (title === 'totalCandidates') return { icon: mdiAbacus, color: 'success' }

    return { icon: mdiAccountOutline, color: 'primary' }
  }

  return {
    fetchJobs,
    fetchRecruitersChoices,
    fetchJobsOverview,
    resolveJobStageColor,
    resolveJobsTotalIcon,
    delaySearchHandler,

    tableColumns,
    searchQuery,
    totalJobsListTable,
    loading,
    options,
    overview,
    jobs,
    choices,
    recruiterChoices,
    stageFilter,
    workflowFilter,
    cityFilter,
    assignedToFilter,
  }
}
